// Tenant support tickets (PRD §4.17). File a ticket, follow the thread, reply.
// Tagnologys staff answer from the /padmin Tickets console.
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import {
  createTicket, listMyTickets, getMyTicket, replyToTicket,
  TICKET_MODULES,
  type MyTicket, type MyTicketReply, type TicketModule, type TicketPriority, type TicketStatus,
} from '../lib/support';

const FLD = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const CARD = 'rounded-card border border-slate-200 bg-white p-5 shadow-sm';
const BTN = 'rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40';
const GHOST = 'rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary';

const PILL: Record<TicketStatus, string> = {
  open: 'bg-amber-100 text-amber-700',
  answered: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-ink-2',
};

function when(ms: number): string {
  return ms ? new Date(ms).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
}

export default function Support() {
  const { t } = useI18n();
  const [tickets, setTickets] = useState<MyTicket[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [replies, setReplies] = useState<MyTicketReply[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // composer
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [mod, setMod] = useState<TicketModule>('general');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [reply, setReply] = useState('');

  const load = useCallback(() => {
    listMyTickets().then(setTickets).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    setReplies(null);
    if (!openId) return;
    // Guard against a slow fetch for ticket A landing after the user opened B.
    let cancelled = false;
    getMyTicket(openId)
      .then((r) => { if (!cancelled) setReplies(r.replies); })
      .catch((e: Error) => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [openId]);

  async function submit() {
    setBusy(true); setErr(null);
    try {
      await createTicket({ subject: subject.trim(), body: body.trim(), module: mod, priority });
      setSubject(''); setBody(''); setMod('general'); setPriority('normal'); setShowNew(false);
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function send() {
    if (!openId || !reply.trim()) return;
    setBusy(true); setErr(null);
    try {
      await replyToTicket(openId, reply.trim());
      setReply('');
      const r = await getMyTicket(openId); setReplies(r.replies);
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const open = tickets?.find((x) => x.id === openId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-extrabold text-ink">{t('sup.title')}</h1>
        <button type="button" onClick={() => setShowNew((v) => !v)} className={cn(BTN, 'ms-auto')}>
          + {t('sup.new')}
        </button>
      </div>
      <p className="text-sm text-ink-3">{t('sup.note')}</p>
      {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}

      {showNew && (
        <div className={cn(CARD, 'space-y-3')}>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('sup.subject')}</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={FLD} />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('sup.area')}</span>
              <select value={mod} onChange={(e) => setMod(e.target.value as TicketModule)} className={FLD}>
                {TICKET_MODULES.map((m) => <option key={m} value={m}>{t(`sup.area.${m}`)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('sup.priority')}</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)} className={FLD}>
                <option value="low">{t('pa.priority.low')}</option>
                <option value="normal">{t('pa.priority.normal')}</option>
                <option value="high">{t('pa.priority.high')}</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('sup.message')}</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className={cn(FLD, 'resize-y')} />
          </label>
          <button type="button" onClick={() => void submit()} disabled={busy || subject.trim().length < 3 || body.trim().length < 3} className={BTN}>
            {busy ? '…' : t('sup.submit')}
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <div className="rounded-card border border-slate-200 bg-white shadow-sm">
          {tickets === null && <div className="py-10 text-center text-sm text-ink-3">…</div>}
          {tickets?.length === 0 && <div className="py-10 text-center text-sm text-ink-3">{t('sup.empty')}</div>}
          {tickets?.map((tk) => (
            <button
              key={tk.id}
              type="button"
              onClick={() => setOpenId(tk.id)}
              className={cn('block w-full border-b border-slate-100 px-4 py-3 text-start last:border-0 hover:bg-surface',
                openId === tk.id && 'bg-surface')}
            >
              <div className="flex items-center gap-2">
                <b className="min-w-0 flex-1 truncate text-[13px] text-ink">{tk.subject}</b>
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', PILL[tk.status])}>
                  {t(`pa.tkstatus.${tk.status}`)}
                </span>
              </div>
              <span className="mt-0.5 block text-[11px] text-ink-3">
                {t(`sup.area.${tk.module}`)} · {when(tk.updatedAt)}
              </span>
            </button>
          ))}
        </div>

        <div className={cn(CARD, 'flex min-h-[320px] flex-col')}>
          {!open ? (
            <div className="m-auto text-sm text-ink-3">{t('sup.pick')}</div>
          ) : (
            <>
              <div className="border-b border-slate-100 pb-3">
                <b className="block text-sm text-ink">{open.subject}</b>
                <span className="text-[11px] text-ink-3">{t(`sup.area.${open.module}`)} · {when(open.createdAt)}</span>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto py-3">
                {replies === null && <div className="py-6 text-center text-sm text-ink-3">…</div>}
                {replies?.map((r) => (
                  <div key={r.id} className={cn('max-w-[85%] rounded-card px-3.5 py-2.5',
                    r.authorRole === 'staff' ? 'bg-surface' : 'ms-auto bg-primary text-white')}>
                    <span className={cn('block text-[10px] font-bold uppercase tracking-wide',
                      r.authorRole === 'staff' ? 'text-ink-3' : 'text-white/70')}>
                      {r.authorRole === 'staff' ? t('sup.fromStaff') : t('sup.fromYou')} · {when(r.createdAt)}
                    </span>
                    <p className={cn('mt-1 whitespace-pre-wrap text-[13px]', r.authorRole === 'staff' ? 'text-ink' : 'text-white')}>
                      {r.body}
                    </p>
                  </div>
                ))}
              </div>
              {open.status !== 'closed' && (
                <div className="space-y-2 border-t border-slate-100 pt-3">
                  <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
                    placeholder={t('sup.replyPlaceholder')} className={cn(FLD, 'resize-y')} />
                  <button type="button" onClick={() => void send()} disabled={busy || !reply.trim()} className={BTN}>
                    {busy ? '…' : t('sup.reply')}
                  </button>
                </div>
              )}
              {open.status === 'closed' && (
                <div className="border-t border-slate-100 pt-3">
                  <span className={GHOST}>{t('sup.closed')}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
